import { useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { ArrowLeft } from "lucide-react"

import { useCreateCreosAsset } from "@/api/hooks/creos"
import { useOfferVerticals } from "@/api/hooks/offers"
import { ApiError } from "@/api/client"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { usePageTitle } from "@/layouts/pageChrome"
import {
  DEFAULT_KLOW_LANGUAGE,
  KLOW_LANGUAGES,
  type KlowLanguageCode,
} from "@/lib/klowLanguages"
import { VERTICAL_PRESETS } from "@/lib/verticalCatalog"

const ASPECT_OPTIONS = [
  { value: "9:16", label: "9:16 (TikTok / Reels)" },
  { value: "1:1", label: "1:1 (Square)" },
] as const

export default function CreosNewPage() {
  usePageTitle("", "CreOS — New")
  const navigate = useNavigate()
  const create = useCreateCreosAsset()

  const [prompt, setPrompt] = useState("")
  const [verticalSlug, setVerticalSlug] = useState("")
  const [language, setLanguage] =
    useState<KlowLanguageCode>(DEFAULT_KLOW_LANGUAGE)
  const [aspectRatio, setAspectRatio] = useState<"9:16" | "1:1">("9:16")
  const [slideCount, setSlideCount] = useState(1)
  const [files, setFiles] = useState<File[]>([])
  const [formError, setFormError] = useState<string | null>(null)

  const verticalsQuery = useOfferVerticals()
  const verticalOptions = useMemo(() => {
    const fromApi = verticalsQuery.data?.data ?? []
    const map = new Map<string, string>()
    for (const v of VERTICAL_PRESETS) map.set(v.slug, v.name)
    for (const v of fromApi) {
      if (v.slug) map.set(v.slug, v.name || v.slug)
    }
    return [...map.entries()]
      .map(([slug, name]) => ({ slug, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [verticalsQuery.data])

  const selectedVertical = verticalOptions.find((v) => v.slug === verticalSlug)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!prompt.trim()) {
      setFormError("Prompt is required")
      return
    }
    if (!selectedVertical) {
      setFormError("Select a vertical")
      return
    }
    if (files.length > 8) {
      setFormError("Maximum 8 reference images")
      return
    }

    try {
      await create.mutateAsync({
        buyer_prompt: prompt.trim(),
        vertical_slug: selectedVertical.slug,
        vertical_name: selectedVertical.name,
        language,
        aspect_ratio: aspectRatio,
        slide_count: slideCount,
        files,
      })
      navigate("/creos")
    } catch (err) {
      const message =
        err instanceof ApiError
          ? String(err.detail ?? err.message)
          : err instanceof Error
            ? err.message
            : "Failed to create request"
      setFormError(message)
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          className="border-[#555] bg-transparent text-white hover:bg-[#333]"
          onClick={() => navigate("/creos")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
          Back
        </Button>
        <h1 className="text-2xl font-semibold text-white">New generation</h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-5 rounded-xl border border-[#4a4a4a] bg-[#2d2d2d] p-6"
      >
        <div className="space-y-2">
          <Label htmlFor="creos-prompt" className="text-[#ccc]">
            Prompt
          </Label>
          <Textarea
            id="creos-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={5}
            placeholder="Describe the creative you want…"
            className="border-[#555] bg-[#3a3a3a] text-white"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-[#ccc]">Vertical</Label>
            <Select value={verticalSlug} onValueChange={setVerticalSlug}>
              <SelectTrigger className="border-[#555] bg-[#3a3a3a] text-white">
                <SelectValue placeholder="Select vertical" />
              </SelectTrigger>
              <SelectContent>
                {verticalOptions.map((v) => (
                  <SelectItem key={v.slug} value={v.slug}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-[#ccc]">Language</Label>
            <Select
              value={language}
              onValueChange={(v) => setLanguage(v as KlowLanguageCode)}
            >
              <SelectTrigger className="border-[#555] bg-[#3a3a3a] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KLOW_LANGUAGES.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-[#ccc]">Aspect ratio</Label>
            <Select
              value={aspectRatio}
              onValueChange={(v) => setAspectRatio(v as "9:16" | "1:1")}
            >
              <SelectTrigger className="border-[#555] bg-[#3a3a3a] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ASPECT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-[#ccc]">Slides (1 = static)</Label>
            <Select
              value={String(slideCount)}
              onValueChange={(v) => setSlideCount(Number(v))}
            >
              <SelectTrigger className="border-[#555] bg-[#3a3a3a] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="creos-refs" className="text-[#ccc]">
            Reference images (optional, max 8)
          </Label>
          <input
            id="creos-refs"
            type="file"
            accept="image/*"
            multiple
            className="block w-full text-sm text-[#ccc] file:mr-3 file:rounded-md file:border-0 file:bg-[#7488e8] file:px-3 file:py-2 file:text-sm file:font-medium file:text-white"
            onChange={(e) => {
              const list = Array.from(e.target.files ?? []).slice(0, 8)
              setFiles(list)
            }}
          />
          {files.length > 0 ? (
            <p className="text-xs text-[#888]">
              {files.length} file{files.length === 1 ? "" : "s"} selected
            </p>
          ) : null}
        </div>

        {formError ? (
          <p className="text-sm text-red-300">{formError}</p>
        ) : null}

        <Button
          type="submit"
          disabled={create.isPending}
          className="w-full bg-[#7488e8] hover:bg-[#687bda]"
        >
          {create.isPending ? "Submitting…" : "Generate"}
        </Button>
      </form>
    </div>
  )
}
